import { createContext, useContext, useRef, useState } from "react"
import { getCreativeAsyncAnalyzeJobStatus, getManyCreativesById } from '@/services/api.js'
import { toast } from 'sonner'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { handleError } from '@/services/errorHandler.js'

export const CreativeAnalyticsContext = createContext(null)

export function CreativeAnalyticsProvider({ children }) {

    const { creativeContext } = useDashboardContext()
    const [adGroupReportsByKey, setAdGroupReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`

    const [superSegmentsDataByKey, setSuperSegmentsDataByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [superSegmentsReportsByKey, setSuperSegmentsReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [naimngConventionValuesBySpaceId, setNamingConventionValuesBySpaceId] = useState({})
    const [campaignsRelevantToAdsBySpaceId, setCampaignsRelevantToAdsBySpaceId] = useState({})
    const [adSetsRelevantToAdsBySpaceId, setAdSetsRelevantToAdsBySpaceId] = useState({})
    const [sourceBySpaceId, setSourceBySpaceId] = useState({})
    const getNamingConventionValuesBySpaceId = (spaceId) => {
        return naimngConventionValuesBySpaceId[spaceId]
    }

    const updateNamingConventionValuesBySpaceId = (spaceId, namingConventionValues) => {
        setNamingConventionValuesBySpaceId(prev => ({ ...prev, [spaceId]: namingConventionValues }))
    }

    const getSourceBySpaceId = (spaceId) => {
        return sourceBySpaceId[spaceId]
    }

    const updateSourceBySpaceId = (spaceId, source) => {
        setSourceBySpaceId(prev => ({ ...prev, [spaceId]: source }))
    }

    const getCampaignsRelevantToAdsBySpaceId = (spaceId) => {
        return campaignsRelevantToAdsBySpaceId[spaceId]
    }

    const updateCampaignsRelevantToAdsBySpaceId = (spaceId, campaignsRelevantToAds) => {
        setCampaignsRelevantToAdsBySpaceId(prev => ({ ...prev, [spaceId]: campaignsRelevantToAds }))
    }

    const getAdSetsRelevantToAdsBySpaceId = (spaceId) => {
        return adSetsRelevantToAdsBySpaceId[spaceId]
    }

    const updateAdSetsRelevantToAdsBySpaceId = (spaceId, adSetsRelevantToAds) => {
        setAdSetsRelevantToAdsBySpaceId(prev => ({ ...prev, [spaceId]: adSetsRelevantToAds }))
    }

    const updateAccountOverviewSummary = (key, data) => setAccountOverviewSummaries(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewTrend = (key, data) => setAccountOverviewTrends(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewBreakdown = (key, data) => setAccountOverviewBreakdowns(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewCountry = (key, data) => setAccountOverviewCountry(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewCampaigns = (key, data) => setAccountOverviewCampaigns(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewAiInsights = (key, data) => setAccountOverviewAiInsights(prev => ({ ...prev, [key]: data }))
    const updateAccountOverviewPreferences = (data) => setAccountOverviewPreferences(data)

    const [customAdGroupReportsByKey, setCustomAdGroupReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [creativeInsightsLight, setCreativeInsightsLight] = useState({}) // Key: "creativeId-MM-YYYY"
    const [creativeInsightsFull, setCreativeInsightsFull] = useState({}) // Key: "creativeId-MM-YYYY"
    const [insightsLightInFlight, setInsightsLightInFlight] = useState(new Set()) // Keys currently being fetched (light)
    const [insightsFullInFlight, setInsightsFullInFlight] = useState(new Set()) // Keys currently being fetched (full)
    const [adsByKey, setAdsByKey] = useState({}) // This is a flat array of all the ads data for the chosen dates by the same key

    // Track ads-fetch keys currently in flight so simultaneous callers (e.g. multiple
    // view widgets sharing the same account+date scope) don't trigger duplicate network
    // requests for the same ads dataset. Ref-based: doesn't cause re-renders.
    //
    // Each key maps to a promise that resolves when its fetch settles. A deduped
    // caller (a second consumer requesting the same scope while a fetch is already
    // running — e.g. CreativePage after the home-page prefetch) awaits this promise
    // so it can show its own loader until the data lands, instead of bailing
    // immediately and rendering an empty state.
    const adsKeysInFlightRef = useRef(new Map()) // key -> { promise, resolve }
    const isAdsKeyInFlight = (key) => adsKeysInFlightRef.current.has(key)
    const getAdsKeyInFlightPromise = (key) => adsKeysInFlightRef.current.get(key)?.promise
    const markAdsKeyInFlight = (key) => {
        if (adsKeysInFlightRef.current.has(key)) {
            return
        }
        let resolve
        const promise = new Promise(r => {
            resolve = r
        })
        adsKeysInFlightRef.current.set(key, { promise, resolve })
    }
    const unmarkAdsKeyInFlight = (key) => {
        const entry = adsKeysInFlightRef.current.get(key)
        if (!entry) {
            return
        }
        entry.resolve()
        adsKeysInFlightRef.current.delete(key)
    }

    // Track errors per cache key so widgets can render an error state instead of
    // looping forever on the loading skeleton when a fetch fails (e.g. wrong
    // integration, expired token, no permissions).
    const [adsKeyErrors, setAdsKeyErrors] = useState({})
    const setAdsKeyError = (key, message) => setAdsKeyErrors(prev => ({ ...prev, [key]: message }))
    const clearAdsKeyError = (key) => setAdsKeyErrors(prev => {
        if (!(key in prev)) {
            return prev
        }
        const next = { ...prev }
        delete next[key]
        return next
    })
    const [insightsByKey, setInsightsByKey] = useState({}) // Insights per creatives set by the same key (Ex. "creative-id_creative-id2")
    const [isPollingAnalyzeProcessStatus, setIsPollingAnalyzeProcessStatus] = useState(false)
    const { updateCreativeState } = creativeContext
    const [videoExplorerDataByKey, setVideoExplorerDataByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [superSegmentsSuggestionsByKey, setSuperSegmentsSuggestionsByKey] = useState({}) // The 'key' will be the creative_ids.join('_')
    const [accountOverviewSummaries, setAccountOverviewSummaries] = useState({})
    const [accountOverviewTrends, setAccountOverviewTrends] = useState({})
    const [accountOverviewBreakdowns, setAccountOverviewBreakdowns] = useState({})
    const [accountOverviewCountry, setAccountOverviewCountry] = useState({})
    const [accountOverviewCampaigns, setAccountOverviewCampaigns] = useState({})
    const [accountOverviewAiInsights, setAccountOverviewAiInsights] = useState({})
    const [accountOverviewPreferences, setAccountOverviewPreferences] = useState(null)

    const getCreativeInsightLight = (key) => creativeInsightsLight[key]

    const setCreativeInsightLight = (key, data) => {
        setCreativeInsightsLight(prev => ({ ...prev, [key]: data }))
    }

    const getCreativeInsightFull = (key) => creativeInsightsFull[key]

    const setCreativeInsightFull = (key, data) => {
        setCreativeInsightsFull(prev => ({ ...prev, [key]: data }))
    }

    // In-flight tracking helpers — prevent duplicate fetches across mount/unmount cycles
    const isInsightLightInFlight = (key) => insightsLightInFlight.has(key)
    const markInsightLightInFlight = (key) => setInsightsLightInFlight(prev => new Set(prev).add(key))
    const unmarkInsightLightInFlight = (key) => setInsightsLightInFlight(prev => {
        const next = new Set(prev); next.delete(key); return next
    })

    const isInsightFullInFlight = (key) => insightsFullInFlight.has(key)
    const markInsightFullInFlight = (key) => setInsightsFullInFlight(prev => new Set(prev).add(key))
    const unmarkInsightFullInFlight = (key) => setInsightsFullInFlight(prev => {
        const next = new Set(prev); next.delete(key); return next
    })

    const getInsightsByKey = (key) => insightsByKey[key]

    const updateInsightsByKey = (key, insightsData) => {
        setInsightsByKey(prev => ({ ...prev, [key]: insightsData }))
    }

    const getReportByGroupType = (key, groupType) => {
        return adGroupReportsByKey[key]?.[groupType] || []
    }

    const updateReportByGroupType = (key, report) => {
        setAdGroupReportsByKey(prev => ({ ...prev, [key]: report }))
    }

    // Merge a single group-type slot into an existing report key without clobbering
    // sibling group types (used by the lazy asset-breakdown group-by).
    const updateReportGroupSlot = (key, groupType, groups) => {
        setAdGroupReportsByKey(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [groupType]: groups } }))
    }

    const getCustomReportByGroupId = (key, groupId) => {
        return customAdGroupReportsByKey[key]?.[groupId]
    }

    const updateCustomReportByGroupIds = (key, groupsData) => {
        const newState = { ...customAdGroupReportsByKey }
        for (const group of groupsData) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][group.id] = group
        }
        setCustomAdGroupReportsByKey(newState)
    }

    const getSuperSegmentsDataByKey = (key, query) => {
        return superSegmentsDataByKey[key]?.[query]
    }

    const updateSuperSegmentsData = (key, superSegmentsData) => {
        const newState = { ...superSegmentsDataByKey }
        for (const superSegment of superSegmentsData) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][superSegment.query] = superSegment
        }
        setSuperSegmentsDataByKey(newState)
    }

    const getSuperSegmentsReportsByKey = (key, query) => {
        return superSegmentsReportsByKey[key]?.[query]
    }

    const updateSuperSegmentsReports = (key, superSegmentsReports) => {
        const newState = { ...superSegmentsReportsByKey }
        for (const report of superSegmentsReports) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][report.id] = report
        }
        setSuperSegmentsReportsByKey(newState)
    }

    const getAdsByKey = (key) => {
        return adsByKey[key]
    }

    const updateAdsByKey = (key, adsData) => {
        setAdsByKey(prev => ({ ...prev, [key]: adsData }))
    }

    // Process of the async ads analyze
    const triggerAnalyzeProcessPolling = async (creativeIds) => {
        try {
            if (isPollingAnalyzeProcessStatus) {
                return
            }
            for (let i = 0; i < 10; i++) {
                const isRunningResponse = await getCreativeAsyncAnalyzeJobStatus()
                if (isRunningResponse.data) {
                    setIsPollingAnalyzeProcessStatus(true)
                    pollAsyncProcessStatus(creativeIds)
                    return
                }
                await new Promise(resolve => setTimeout(resolve, 2000))
            }
        } catch (e) {
            handleError(e)
            console.log(e)
        }
    }

    const pollAsyncProcessStatus = async (creativeIds) => {
        try {
            // poll for 5 minutes max
            for (let i = 0; i < 100; i++) {
                const isStillRunningResponse = await getCreativeAsyncAnalyzeJobStatus()
                if (!isStillRunningResponse.data) {
                    setIsPollingAnalyzeProcessStatus(false)
                    const creativesResponse = await getManyCreativesById(creativeIds)
                    updateCreativeState(creativesResponse.data)
                    toast.success('Done analyzing your creatives')
                    return
                }
                await new Promise(resolve => setTimeout(resolve, 3000))
            }
        } catch (e) {
            handleError(e)
            console.log(e)
        }
    }

    // We use this to clear the ads releat data when updating subscription
    const clearAdsDataState = () => {
        setAdsByKey(prev => ({}))
        setAdGroupReportsByKey(prev => ({}))
        setCustomAdGroupReportsByKey(prev => ({}))
    }


    return (
        <CreativeAnalyticsContext.Provider
            value={{
                triggerAnalyzeProcessPolling,
                isPollingAnalyzeProcessStatus,
                superSegmentsDataByKey,
                getSuperSegmentsDataByKey,
                updateSuperSegmentsData,
                superSegmentsReportsByKey,
                getSuperSegmentsReportsByKey,
                updateSuperSegmentsReports,
                adGroupReportsByKey,
                getReportByGroupType,
                getCustomReportByGroupId,
                updateCustomReportByGroupIds,
                getAdsByKey,
                updateAdsByKey,
                isAdsKeyInFlight,
                getAdsKeyInFlightPromise,
                markAdsKeyInFlight,
                unmarkAdsKeyInFlight,
                adsKeyErrors,
                setAdsKeyError,
                clearAdsKeyError,
                videoExplorerDataByKey,
                setVideoExplorerDataByKey,
                updateReportByGroupType,
                updateReportGroupSlot,
                adsByKey,
                creativeInsightsLight,
                getCreativeInsightLight,
                setCreativeInsightLight,
                getCreativeInsightFull,
                setCreativeInsightFull,
                isInsightLightInFlight,
                markInsightLightInFlight,
                unmarkInsightLightInFlight,
                isInsightFullInFlight,
                markInsightFullInFlight,
                unmarkInsightFullInFlight,
                getInsightsByKey,
                updateInsightsByKey,
                superSegmentsSuggestionsByKey,
                setSuperSegmentsSuggestionsByKey,
                clearAdsDataState,
                getNamingConventionValuesBySpaceId,
                updateNamingConventionValuesBySpaceId,
                getCampaignsRelevantToAdsBySpaceId,
                updateCampaignsRelevantToAdsBySpaceId,
                getAdSetsRelevantToAdsBySpaceId,
                updateAdSetsRelevantToAdsBySpaceId,
                getSourceBySpaceId,
                updateSourceBySpaceId,
                accountOverviewSummaries,
                updateAccountOverviewSummary,
                accountOverviewTrends,
                updateAccountOverviewTrend,
                accountOverviewBreakdowns,
                updateAccountOverviewBreakdown,
                accountOverviewCountry,
                updateAccountOverviewCountry,
                accountOverviewCampaigns,
                updateAccountOverviewCampaigns,
                accountOverviewAiInsights,
                updateAccountOverviewAiInsights,
                accountOverviewPreferences,
                updateAccountOverviewPreferences
            }}>
            {children}
        </CreativeAnalyticsContext.Provider>
    )
}

export const useCreativeAnalyticsContext = () => {
    const context = useContext(CreativeAnalyticsContext)

    if (!context) {
        // Return safe no-op fallback for read-only / snapshot usage outside the provider
        return {
            getCreativeInsightLight: () => null,
            setCreativeInsightLight: () => { },
            getCreativeInsightFull: () => null,
            setCreativeInsightFull: () => { },
            getInsightsByKey: () => null,
            isInsightLightInFlight: () => false,
            markInsightLightInFlight: () => { },
            unmarkInsightLightInFlight: () => { },
            isInsightFullInFlight: () => false,
            markInsightFullInFlight: () => { },
            unmarkInsightFullInFlight: () => { },
        }
    }

    return context;
}